/** List (row) view of an event's media — an alternative to the grid. */
import { IconCheck, IconPhoto, IconVideo } from '@tabler/icons-react';
import { Media } from '../media/Media.tsx';
import type { MediaItem } from '../../lib/types.ts';
import { formatSize } from '../../lib/media.ts';
import { cn } from '../../lib/cn.ts';

interface FileListTableProps {
  items: MediaItem[];
  selection: Set<string>;
  selectionActive: boolean;
  onOpen: (index: number) => void;
  onToggleSelect: (key: string) => void;
}

function formatDate(ts: number | null): string {
  if (ts == null) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function FileListTable({
  items,
  selection,
  selectionActive,
  onOpen,
  onToggleSelect,
}: FileListTableProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
      <ul className="divide-y divide-line">
        {items.map((item, i) => {
          const selected = selection.has(item.key);
          return (
            <li
              key={item.key}
              className={cn(
                'flex items-center gap-3 px-3 py-2 transition duration-200 ease-standard',
                selected ? 'bg-accent/10' : 'hover:bg-grid',
              )}
            >
              <button
                onClick={() => onToggleSelect(item.key)}
                aria-label={selected ? `Deselect ${item.displayName}` : `Select ${item.displayName}`}
                aria-pressed={selected}
                className={cn(
                  'grid h-5 w-5 shrink-0 place-items-center rounded border transition duration-200 ease-standard',
                  selected
                    ? 'border-accent bg-accent text-white'
                    : 'border-line-2 text-transparent',
                  !selected && !selectionActive && 'opacity-40',
                )}
              >
                <IconCheck className="h-3.5 w-3.5" />
              </button>

              <button
                onClick={() => onOpen(i)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                aria-label={`Open ${item.displayName}`}
              >
                <span className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg">
                  <Media
                    fileKey={item.thumbnailKey}
                    kind={item.kind}
                    alt={item.displayName}
                    className="h-12 w-12"
                  />
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                  {item.kind === 'video' ? (
                    <IconVideo className="h-3.5 w-3.5 shrink-0 text-muted" />
                  ) : (
                    <IconPhoto className="h-3.5 w-3.5 shrink-0 text-muted" />
                  )}
                  <span className="truncate text-sm text-fg">
                    {item.displayName}
                  </span>
                </span>
              </button>

              <span className="hidden w-28 shrink-0 text-right text-xs text-muted sm:block">
                {formatDate(item.timestamp)}
              </span>
              <span className="w-16 shrink-0 text-right text-xs text-muted">
                {formatSize(item.size)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
